import { trackRetailerOnboardingTopPromoBannerView } from "@faire/web-api/events/retailerOnboarding/view/topPromoBanner";
import insiderMarketingPage from "@faire/web-api/routes/user/insider/info";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { Span } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useEffect, type JSX } from "react";
import styled from "styled-components";

import { CountdownBanner } from "@faire/retailer/components/CountdownBanner/CountdownBanner";
import { PromoBannerType } from "@faire/retailer/components/Notifications/PromoBanner/bannerOrder";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import {
  MarketStore,
  MarketState,
  RetailerSegment,
} from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

// displayed when the user is logged out
const renderLoggedOutView = (_lastDay: boolean, mainEventRangeDate: string) => {
  const handleSignUp = () =>
    popSignUp(SignUpType.GENERIC, () => null, {
      element: SignUpElement.TOP_BANNER,
      sourceId: "market_countdown_banner",
    });

  if (
    MarketStore.get().currentStateIncludingLoggedOut === MarketState.MAIN_EVENT
  ) {
    // Main event
    return (
      <StrictLocalMsg
        defaultMessage="<MediumFormatting>Shop now!</MediumFormatting> Get up to 30% off at {marketEventName} through {eventEndDate}. <LinkFormatting>Sign up to shop</LinkFormatting>"
        values={{
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
          marketEventName: MarketStore.get().marketName,
          eventEndDate: MarketStore.get().formattedLastDay,
          LinkFormatting: (chunks: JSX.Element) => (
            <Link onClick={handleSignUp}>{chunks}</Link>
          ),
        }}
      />
    );
  } else if (
    MarketStore.get().currentStateIncludingLoggedOut ===
    MarketState.EARLY_ACCESS
  ) {
    // Early access day
    return (
      <StrictLocalMsg
        defaultMessage="Get up to 30% off at {marketEventName} this week! <LinkFormatting>Sign up to shop</LinkFormatting>"
        values={{
          marketEventName: MarketStore.get().marketName,
          LinkFormatting: (chunks: JSX.Element) => (
            <Link onClick={handleSignUp}>{chunks}</Link>
          ),
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
        }}
      />
    );
  }

  // Pre-market
  return (
    <StrictLocalMsg
      data-test-id="countdown-pre-market-logged-out"
      defaultMessage="Get up to 30% off at {marketEventName}, {mainEventRangeDate}! <LinkFormatting>Sign up to shop</LinkFormatting>"
      values={{
        marketEventName: MarketStore.get().marketName,
        mainEventRangeDate,
        LinkFormatting: (chunks: JSX.Element) => (
          <Link onClick={handleSignUp}>{chunks}</Link>
        ),
        MediumFormatting: (chunks: JSX.Element) => (
          <MediumText>{chunks}</MediumText>
        ),
      }}
    />
  );
};

// displayed when there is >= 24 hours before the start or end of the event
const renderDaysBeforeDeadlineView = (
  daysBeforeNumber: number,
  mainEventRangeDate: string
) => {
  const { retailerSegment, showAllSpecials } = MarketStore.get();

  if (!MarketStore.get().isLiveForCountdownBanner) {
    if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
      // Pre-market. Enrolled
      return showAllSpecials ? (
        <StrictLocalMsg
          defaultMessage="Just <MediumFormatting>{daysBeforeNumber} {daysBeforeNumber, plural, one {day} other {days}}</MediumFormatting> until Insiders get early access to {marketEventName}! <LinkFormatting>Start planning</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            daysBeforeNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={MarketStore.get().marketSpecialsRoute}>
                {chunks}
              </Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      ) : (
        <StrictLocalMsg
          defaultMessage="Just <MediumFormatting>{daysBeforeNumber} {daysBeforeNumber, plural, one {day} other {days}}</MediumFormatting> until Insiders get early access to {marketEventName}!"
          values={{
            marketEventName: MarketStore.get().marketName,
            daysBeforeNumber,
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
      // Pre-market. Eligible
      return (
        <StrictLocalMsg
          defaultMessage="Just <MediumFormatting>{daysBeforeNumber} {daysBeforeNumber, plural, one {day} other {days}}</MediumFormatting> until Insiders get early access to {marketEventName}! <LinkFormatting>Become an Insider</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            daysBeforeNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={insiderMarketingPage()}>{chunks}</Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    }
    // Pre-market. Non-enrolled
    return showAllSpecials ? (
      <StrictLocalMsg
        defaultMessage="Get up to 30% off at {marketEventName}, {mainEventRangeDate}! <LinkFormatting>Start planning</LinkFormatting>"
        values={{
          marketEventName: MarketStore.get().marketName,
          mainEventRangeDate,
          LinkFormatting: (chunks: JSX.Element) => (
            <Link linkTo={MarketStore.get().marketSpecialsRoute}>{chunks}</Link>
          ),
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Get up to 30% off at {marketEventName}, {mainEventRangeDate}!"
        values={{
          marketEventName: MarketStore.get().marketName,
          mainEventRangeDate,
        }}
      />
    );
  }

  // During market
  return showAllSpecials ? (
    <StrictLocalMsg
      defaultMessage="Just <MediumFormatting>{daysBeforeNumber} {daysBeforeNumber, plural, one {day} other {days}}</MediumFormatting> left to shop the best deals at {marketEventName}. <LinkFormatting>Shop now</LinkFormatting>"
      values={{
        marketEventName: MarketStore.get().marketName,
        daysBeforeNumber,
        LinkFormatting: (chunks: JSX.Element) => (
          <Link linkTo={MarketStore.get().marketSpecialsRoute}>{chunks}</Link>
        ),
        MediumFormatting: (chunks: JSX.Element) => (
          <MediumText>{chunks}</MediumText>
        ),
      }}
    />
  ) : (
    <StrictLocalMsg
      defaultMessage="Just <MediumFormatting>{daysBeforeNumber} {daysBeforeNumber, plural, one {day} other {days}}</MediumFormatting> left to shop the best deals at {marketEventName}."
      values={{
        marketEventName: MarketStore.get().marketName,
        daysBeforeNumber,
        MediumFormatting: (chunks: JSX.Element) => (
          <MediumText>{chunks}</MediumText>
        ),
      }}
    />
  );
};

// displayed when there is < 24 hours before the start or end of the event
const renderHoursBeforeDeadlineView = (
  timeRemainingTime: string,
  hoursLeftNumber: number
) => {
  const { retailerSegment, showAllSpecials } = MarketStore.get();

  if (MarketStore.get().isEarlyAccessDay) {
    if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
      // Early access day. Enrolled Insider
      return showAllSpecials ? (
        <StrictLocalMsg
          defaultMessage="Hey Insider! Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> left for exclusive early access to {marketEventName}. <LinkFormatting>Shop now</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={MarketStore.get().marketSpecialsRoute}>
                {chunks}
              </Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      ) : (
        <StrictLocalMsg
          defaultMessage="Hey Insider! Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> left for exclusive early access to {marketEventName}."
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
      // Early access day. Eligible
      return (
        <StrictLocalMsg
          defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until {marketEventName}! Join Insider to start shopping now. <LinkFormatting>Become an Insider</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={insiderMarketingPage()}>{chunks}</Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    }

    // Early access day. Non-eligible
    return showAllSpecials ? (
      <StrictLocalMsg
        defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until {marketEventName} opens! <LinkFormatting>Start planning</LinkFormatting>"
        values={{
          marketEventName: MarketStore.get().marketName,
          hoursLeftNumber,
          LinkFormatting: (chunks: JSX.Element) => (
            <Link linkTo={MarketStore.get().marketSpecialsRoute}>{chunks}</Link>
          ),
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until {marketEventName} opens!"
        values={{
          marketEventName: MarketStore.get().marketName,
          hoursLeftNumber,
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
        }}
      />
    );
  }

  if (!MarketStore.get().isLiveForCountdownBanner) {
    if (retailerSegment === RetailerSegment.INSIDER_ENROLLED) {
      // Pre-market. Enrolled
      return showAllSpecials ? (
        <StrictLocalMsg
          defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until Insiders get early access to {marketEventName}! <LinkFormatting>Start planning</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={MarketStore.get().marketSpecialsRoute}>
                {chunks}
              </Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      ) : (
        <StrictLocalMsg
          defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until Insiders get early access to {marketEventName}!"
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    } else if (retailerSegment === RetailerSegment.INSIDER_ELIGIBLE) {
      // Pre-market. Eligible
      return (
        <StrictLocalMsg
          defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until Insiders get early access to {marketEventName}! <LinkFormatting>Become an Insider</LinkFormatting>"
          values={{
            marketEventName: MarketStore.get().marketName,
            hoursLeftNumber,
            LinkFormatting: (chunks: JSX.Element) => (
              <Link linkTo={insiderMarketingPage()}>{chunks}</Link>
            ),
            MediumFormatting: (chunks: JSX.Element) => (
              <MediumText>{chunks}</MediumText>
            ),
          }}
        />
      );
    }

    // Pre-market. Non-enrolled
    return showAllSpecials ? (
      <StrictLocalMsg
        defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until {marketEventName}! <LinkFormatting>Start planning</LinkFormatting>"
        values={{
          marketEventName: MarketStore.get().marketName,
          hoursLeftNumber,
          LinkFormatting: (chunks: JSX.Element) => (
            <Link linkTo={MarketStore.get().marketSpecialsRoute}>{chunks}</Link>
          ),
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Only <MediumFormatting>{hoursLeftNumber} {hoursLeftNumber, plural, one {hour} other {hours}}</MediumFormatting> until {marketEventName}!"
        values={{
          marketEventName: MarketStore.get().marketName,
          hoursLeftNumber,
          MediumFormatting: (chunks: JSX.Element) => (
            <MediumText>{chunks}</MediumText>
          ),
        }}
      />
    );
  }

  // During market
  return showAllSpecials ? (
    <StrictLocalMsg
      defaultMessage="Just <MediumFormatting>{timeRemainingTime}</MediumFormatting> left to shop the best deals at {marketEventName}. <LinkFormatting>Shop now</LinkFormatting>"
      values={{
        marketEventName: MarketStore.get().marketName,
        timeRemainingTime,
        LinkFormatting: (chunks: JSX.Element) => (
          <Link linkTo={MarketStore.get().marketSpecialsRoute}>{chunks}</Link>
        ),
        MediumFormatting: (chunks: JSX.Element) => (
          <MediumText>{chunks}</MediumText>
        ),
      }}
    />
  ) : (
    <StrictLocalMsg
      defaultMessage="Just <MediumFormatting>{timeRemainingTime}</MediumFormatting> left to shop the best deals at {marketEventName}."
      values={{
        marketEventName: MarketStore.get().marketName,
        timeRemainingTime,
        MediumFormatting: (chunks: JSX.Element) => (
          <MediumText>{chunks}</MediumText>
        ),
      }}
    />
  );
};

const MarketCountdownBannerBase: React.FC = () => {
  useEffect(() => {
    // Special case banner that is not part of the normal banner rotation
    trackRetailerOnboardingTopPromoBannerView(PromoBannerType.MARKET_COUNTDOWN);
  }, []);

  return (
    <CountdownBanner
      textColor={eventsThemeColors.faireMarket.promoText}
      renderDaysBeforeDeadlineView={renderDaysBeforeDeadlineView}
      renderHoursBeforeDeadlineView={renderHoursBeforeDeadlineView}
      renderLoggedOutView={renderLoggedOutView}
      centerAlignIcon
    />
  );
};

export const MarketCountdownBanner = observer(MarketCountdownBannerBase);

const Link = styled(EmbeddedLink).attrs({
  color: eventsThemeColors.faireMarket.promoText,
})``;

const MediumText = styled(Span).attrs({
  variant: "paragraphSansMedium",
  color: eventsThemeColors.faireMarket.promoText,
})``;
